import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import { useUIStore } from '../stores/uiStore';
import { useAuthStore } from '../stores/authStore';
import { useSettingsStore } from '../stores/settingsStore';
import { authService } from '../services/auth.service';
import LoadingOverlay from '../components/common/LoadingOverlay';
import AssumeIdentityBanner from '../components/common/AssumeIdentityBanner';
import IdleWarningToast from '../components/common/IdleWarningToast';
import { useIdleTimeout } from '../hooks/useIdleTimeout';

const DashboardLayout = () => {
  const { isSidebarOpen } = useUIStore();
  const { user, setUser } = useAuthStore();
  const fetchSecurity = useSettingsStore((s) => s.fetchSecurity);
  const securityLoaded = useSettingsStore((s) => s.security !== null);

  // VSNP-704: activity-based idle session timeout. The hook reads
  // sessionManagement.idleTimeoutMinutes from settingsStore and resets
  // on user input; redirects to /login?reason=idle on expiry.
  useIdleTimeout();

  // VSNP-704 follow-up: load the security category so the idle hook
  // sees the operator's configured value. Only fetch once per session
  // — the SecuritySettingsTab pushes updates back to the store on save
  // (see SystemSettings.tsx) so we don't need to poll.
  useEffect(() => {
    if (!securityLoaded) {
      fetchSecurity();
    }
  }, [securityLoaded, fetchSecurity]);

  // Fetch user info on mount if not already loaded
  useEffect(() => {
    if (!user) {
      authService.getUserInfo()
        .then((userData) => {
          setUser(userData);
        })
        .catch((err) => {
          console.error('Failed to fetch user info:', err);
        });
    }
  }, [user, setUser]);

  const { isAssumedIdentity } = useAuthStore();

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-100">
      {/* Assumed identity banner — above everything */}
      {isAssumedIdentity() && <AssumeIdentityBanner />}

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {isSidebarOpen && (
          <aside className="w-56 bg-primary-900 text-white flex-shrink-0 overflow-y-auto">
            <Sidebar />
          </aside>
        )}

        {/* Main content area */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Header */}
          <Header />

          {/* Main content */}
          <main className="flex-1 overflow-y-auto p-6">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Global top progress bar. LoadingOverlay handles its own
          show-delay + min-visible timing internally so it stays mounted
          and simply decides when to render the 2px sweep line. */}
      <LoadingOverlay />

      {/* Idle-timeout warning — appears 60s before session expiry, any
          activity (incl. the dismiss button) resets the timer. */}
      <IdleWarningToast />
    </div>
  );
};

export default DashboardLayout;


